import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { ModalRef } from '@celerofinancas/ui-modals';
import {
  CreditLineModalData,
  CreditLineDetailsData,
  DynamicModalConfig,
  ModalRow,
} from './modal-content.interface';
import { ShimmerComponent } from '@shared/components/shimmer/shimmer.component';
import { TooltipDirective } from '@celerofinancas/ui-directives';

/**
 * Credit line details modal content component
 * Contains ONLY the content, no modal structure
 * Used as content for ModalService
 */
@Component({
  standalone: true,
  selector: 'caixa-webcomponent-credit-line-details-modal',
  templateUrl: './modal-content.component.html',
  styleUrls: ['./modal-content.component.scss'],
  imports: [
    TooltipDirective,
    ShimmerComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreditLineDetailsModalContentComponent {
  /**
   * Error image source
   */
  public readonly errorImageSrc = '/assets/common/light/shared/error-image.svg';

  /**
   * Error state title
   */
  public readonly errorTitle = 'Tivemos um problema ao carregar as informações';

  /**
   * Error state subtitle
   */
  public readonly errorSubtitle = 'Não foi possível exibir os dados agora. Por favor, atualize a página e tente novamente.';

  /**
   * Modal reference injected from the modal service
   */
  public readonly modalRef = inject(ModalRef) as ModalRef<
    unknown,
    CreditLineModalData
  >;

  /**
   * Credit line data passed to the modal
   */
  public get creditLineData(): CreditLineDetailsData | null {
    return this.modalRef.data?.creditLineData?.() ?? null;
  }

  /**
   * Modal loading state
   */
  public get isLoading(): boolean {
    return this.modalRef.data?.loading?.() ?? false;
  }

  /**
   * Modal error state
   */
  public get hasError(): boolean {
    return this.modalRef.data?.hasError?.() ?? false;
  }

  /**
   * Dynamic modal configuration
   */
  public get modalConfig(): DynamicModalConfig {
    const baseRows: ModalRow[] = [
      {
        items: [
          { label: 'Tipo do produto', key: 'productType' },
          { label: 'Número do contrato', key: 'contractNumber' },
          { label: 'Tipo de pagamento', key: 'paymentType' },
        ]
      },
      {
        items: [
          { label: 'Valor contratado', key: 'contractAmount' },
          { label: 'Valor devedor do contrato', key: 'contractOutstandingBalance', format: 'debit' },
        ]
      },
      {
        items: [
          { label: 'Tipo de Taxa', key: 'taxType', tooltip: '<b>Taxa nominal:</b> Percentual de juros sem considerar a frequência de cobrança.<br><b>Taxa efetiva:</b> A forma como os juros são aplicados no tempo, refletindo o custo real da operação.' },
          { label: 'Tipo de Juros', key: 'interestRateType', tooltip: '<b>Juros simples:</b> São calculados apenas sobre o valor inicial.<br><b>Juros compostos:</b> São calculados sobre o valor inicial e os juros acumulados ao longo do tempo.' },
          { label: 'Taxa aplicada', key: 'appliedRate' },
        ]
      },
      {
        items: [
          { label: 'Data da contratação', key: 'contractDate' },
          { label: 'Data final do contrato', key: 'dueDate' },
          { label: 'Status do contrato', key: 'contractStatus' },
        ]
      }
    ];

    if (this.creditLineData?.hasInstallments) {
      baseRows.push({
        sectionTitle: 'Informações do parcelamento do contrato',
        items: [
          { label: 'Parcelas contratadas', key: 'contractedInstallments' },
          { label: 'Parcelas pagas', key: 'paidInstalments' },
          { label: 'Parcelas à pagar', key: 'installmentsToPay' },
        ]
      });

      baseRows.push({
        items: [
          { label: 'Parcelas vencidas', key: 'overdueInstallments' },
          { label: 'Data da última parcela paga', key: 'lastPaidInstallmentDate' },
          { label: 'Valor da última parcela paga', key: 'lastPaidInstallmentAmount', format: 'credit' },
        ]
      });
    }

    return {
      title: 'Detalhe da Linha de Crédito',
      sectionTitle: 'Informações da Linha de Crédito',
      rows: baseRows
    };
  }

  /**
   * Gets details value by key
   *
   * @param key - Property key to get value from
   * @returns Formatted value or fallback
   */
  public getDetailValue(key: keyof CreditLineDetailsData): string {
    if (!this.creditLineData) {
      return 'N/A';
    }

    const value = this.creditLineData[key];
    return value == null || value === '' ? 'N/A' : String(value);
  }
}
